import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { productData, featuresData } from '../mock';
import { Flame, Sprout, Package, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const iconMap = {
  flame: Flame,
  sprout: Sprout,
  fire: Flame,
  package: Package
};

const Home = () => {
  const [email, setEmail] = useState('');

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('Thanks! We\'ll notify you before the next batch drops.');
      setEmail('');
    }
  };

  const HeatIndicator = ({ level }) => {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-300">Heat Level:</span>
        <div className="flex gap-1">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className={`w-6 h-6 rounded ${
                i < level
                  ? 'bg-gradient-to-t from-red-600 to-orange-400'
                  : 'bg-gray-700'
              }`}
            />
          ))}
        </div>
        <span className="text-sm font-bold text-orange-400">{level}/10</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background gradient glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-black to-orange-900/20" />
        
        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_hhh-craft-heat/artifacts/mmf9qjjm_logo.png"
              alt="HHHS Logo"
              className="w-64 h-64 mx-auto mb-8 animate-pulse-slow"
            />
          </div>
          
          <h1 className="text-6xl md:text-8xl font-black mb-6 tracking-tight">
            We Put the <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500">HOT</span> in Hot Sauce
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto leading-relaxed">
            Small batch. Limited edition. Seed to sauce.
          </p>
          
          <p className="text-lg md:text-xl text-orange-400 mb-12 font-medium">
            Once it's gone — it's gone forever.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold px-8 py-6 text-lg transition-all duration-300 transform hover:scale-105"
              >
                Shop Current Batch
              </Button>
            </Link>
            
            <Link to="/about">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black font-bold px-8 py-6 text-lg transition-all duration-300"
              >
                Our Story
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-black text-center mb-16">
            What Makes Us <span className="text-orange-500">Different</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuresData.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={index} className="bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-300 hover:scale-105">
                  <CardContent className="p-6">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-3 text-white">
                      {feature.title}
                    </h3>
                    <p className="text-gray-300 text-center leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Current Batch Spotlight */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-black text-center mb-16">
            Current Batch <span className="text-red-500">Spotlight</span>
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Product Image/Video */}
            <div className="relative group max-w-2xl mx-auto lg:mx-0">
              <div className="absolute inset-0 bg-gradient-to-r from-red-600/30 to-orange-600/30 rounded-lg blur-2xl group-hover:blur-3xl transition-all duration-300" />
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <video 
                  src={productData.images[0]}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto max-h-[850px] object-contain transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
            
            {/* Product Info */}
            <div>
              <div className="inline-block bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                LIMITED EDITION
              </div>
              
              <h3 className="text-4xl font-black mb-2">{productData.name}</h3>
              <p className="text-2xl text-orange-400 font-medium mb-6">{productData.subtitle}</p>
              
              <HeatIndicator level={productData.heatLevel} />
              
              <div className="my-6 border-t border-gray-700" />
              
              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-400 mb-2">PEPPER TYPES:</h4>
                <div className="flex flex-wrap gap-2">
                  {productData.pepperTypes.map((pepper, idx) => (
                    <span key={idx} className="bg-gray-800 text-orange-400 px-3 py-1 rounded-full text-sm font-medium">
                      {pepper}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-400 mb-2">FLAVOR NOTES:</h4>
                <div className="flex flex-wrap gap-2">
                  {productData.flavorNotes.map((note, idx) => (
                    <span key={idx} className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm">
                      {note}
                    </span>
                  ))}
                </div>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                {productData.description}
              </p>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-4xl font-bold text-white">${productData.price.toFixed(2)}</span>
                  <span className="text-gray-400">Local Pickup</span>
                </div>
                <p className="text-sm text-orange-400 mb-3">
                  Visit shop page for shipping options (Canada & USA available)
                </p>
                <span className="text-orange-400 font-medium">
                  Only {productData.currentStock} of {productData.originalStock} left
                </span>
              </div>
              
              <Link to="/shop">
                <Button 
                  size="lg"
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold text-lg py-6 transition-all duration-300 transform hover:scale-105"
                >
                  Shop Now
                </Button>
              </Link>

              {/* Additional Product Videos */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-lg overflow-hidden bg-gray-900 shadow-lg">
                  <video 
                    src="https://customer-assets.emergentagent.com/job_hhh-craft-heat/artifacts/bzlnre01_WhatsApp%20Video%202026-02-18%20at%203.15.42%20AM.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto max-h-[350px] object-contain"
                  />
                </div>
                <div className="rounded-lg overflow-hidden bg-gray-900 shadow-lg">
                  <video 
                    src="https://customer-assets.emergentagent.com/job_hhh-craft-heat/artifacts/xpxn7wfj_WhatsApp%20Video%202026-02-18%20at%203.11.23%20AM.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto max-h-[350px] object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Feed */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <Instagram className="w-12 h-12 mx-auto mb-4 text-orange-500" />
            <h2 className="text-5xl font-black mb-4">
              Follow Our <span className="text-orange-500">Journey</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">@chemistry.dj</p>
            
            <a 
              href="https://instagram.com/chemistry.dj" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black font-bold px-8 py-6 text-lg transition-all duration-300"
              >
                Follow Us on Instagram
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Email Signup */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Don't Miss the <span className="text-red-500">Next Drop</span>
          </h2>
          <p className="text-gray-300 text-lg mb-8 leading-relaxed">
            Get notified before the next batch drops. Limited quantities mean they sell out fast.
          </p>
          
          <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input 
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-14 text-lg"
            />
            <Button 
              type="submit"
              size="lg"
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold px-8 h-14 text-lg"
            >
              Notify Me
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
